import React from 'react';
import { Field } from '../../Field/Field';
import { RadioGroup, RadioGroupProps } from '../../Components/RadioGroup';
import { FieldContainer } from '../../Components/FieldContainer';
import classNames from 'classnames';
import styles from './styles.module.scss';
import { FieldRenderProps } from '../../Field';
import { Label } from '../../Components';

type Props<Options extends string> = {
    id?: string;
    name: string;
    label: string;
    options: Record<Options, string>;
    description?: string;
    initialValue?: Options;
    disabled?: boolean;
};

type AllowedFieldProps<Options> = Omit<
    FieldRenderProps<Options>,
    'name' | 'memoChild' | 'children' | 'initialValue' | 'onBlurValidate'
>;

type AllowedRadioGroupProps<Options> = Omit<
    RadioGroupProps<Options>,
    'options' | 'value' | 'onChange' | 'comparitor' | 'children'
>;

export type RadioFieldProps<Options extends string> = AllowedFieldProps<Options> &
    AllowedRadioGroupProps<Options> &
    Props<Options>;

export const RadioField = <Options extends string>({
    id,
    name,
    label,
    options,
    description,
    initialValue,
    onChangeValidate,
    onSubmitValidate,
    required = false,
    disabled,
    ...radioGroupProps
}: RadioFieldProps<Options>) => {
    const fieldProps: Omit<FieldRenderProps<Options>, 'children'> = {
        id,
        name,
        description,
        initialValue,
        onChangeValidate,
        onSubmitValidate,
        required,
    };

    return (
        <Field<Options> {...fieldProps}>
            {({
                fieldId,
                value,
                setValue,
                errors,
                isValid,
                errorId,
                descriptionId,
                required,
                requiredStrategy,
            }) => {
                const optionKeys = Object.keys(options) as Options[];

                return (
                    <FieldContainer
                        descriptionId={descriptionId}
                        description={description}
                        errorId={errorId}
                        errors={errors}
                    >
                        <Label
                            htmlFor={fieldId}
                            required={required}
                            requiredStrategy={requiredStrategy}
                        >
                            {label}
                        </Label>
                        <RadioGroup<Options>
                            {...radioGroupProps}
                            id={fieldId}
                            options={optionKeys}
                            value={value}
                            onChange={(nextValue) => setValue(nextValue)}
                            className={styles.radioGroup}
                            aria-label={label}
                            aria-invalid={!isValid}
                            aria-errormessage={errorId}
                            aria-describedby={descriptionId}
                            required={required}
                            disabled={disabled}
                        >
                            {({ option, isFocused, isSelected }) => {
                                const className = classNames({
                                    [styles.option]: true,
                                    [styles.selected]: isSelected,
                                });

                                return (
                                    <div className={className} aria-label={options[option]}>
                                        <div className={styles.radio}>
                                            <div className={styles.radioChecked}></div>
                                        </div>
                                        <span>{options[option]}</span>
                                    </div>
                                );
                            }}
                        </RadioGroup>
                    </FieldContainer>
                );
            }}
        </Field>
    );
};
